import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Button, Space, Typography, Empty, Spin, Divider } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, RedoOutlined, SaveOutlined, DownloadOutlined } from '@ant-design/icons';

const { Text } = Typography;

export default function PreviewPanel({
  svgContent,
  signInfo,
  isLoading,
  previewRefreshing,
  onSaveSVG,
  projectId,
  savingSVG,
  exportLocked,
  livePreviewHint,
}) {
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef(null);

  // Define zoom handlers first
  const handleZoomIn = useCallback(() => setZoom(z => Math.min(z * 1.25, 5)), []);
  const handleZoomOut = useCallback(() => setZoom(z => Math.max(z * 0.8, 0.2)), []);
  const handleZoomReset = useCallback(() => setZoom(1), []);

  useEffect(() => {
    // Auto-fit the SVG when it loads
    if (svgContent && containerRef.current) {
      // Reset zoom to 1, but the SVG will auto-scale to fit container
      setZoom(1);
    }
  }, [svgContent]);

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input, textarea, or contenteditable
      const target = e.target;
      const isInput = target.tagName === 'INPUT' || 
                      target.tagName === 'TEXTAREA' || 
                      target.isContentEditable;
      
      if (isInput) return;

      // Check for Ctrl (Windows/Linux) or Cmd (Mac)
      const isModifierPressed = e.ctrlKey || e.metaKey;

      if (isModifierPressed) {
        // Zoom In: Ctrl/Cmd + Plus or Ctrl/Cmd + =
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          handleZoomIn();
        }
        // Zoom Out: Ctrl/Cmd + Minus
        else if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          handleZoomOut();
        }
        // Reset Zoom: Ctrl/Cmd + 0
        else if (e.key === '0') {
          e.preventDefault();
          handleZoomReset();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleZoomIn, handleZoomOut, handleZoomReset]);

  const handleDownload = () => {
    if (!svgContent) return;
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `road_sign_irc67_${Date.now()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const svgDataUrl = useMemo(() => {
    if (!svgContent) return null;
    
    // Make SVG responsive by ensuring viewBox exists and making width/height responsive
    let svg = svgContent;
    const vbMatch = svg.match(/<svg[^>]*\sviewBox="([^"]+)"/);
    const wMatch = svg.match(/<svg[^>]*\swidth="([^"]+)"/);
    const hMatch = svg.match(/<svg[^>]*\sheight="([^"]+)"/);
    
    // Add viewBox if missing
    if (!vbMatch && wMatch && hMatch) {
      try {
        const w = parseFloat(wMatch[1]);
        const h = parseFloat(hMatch[1]);
        if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
          svg = svg.replace(/<svg([^>]*)>/, `<svg$1 viewBox="0 0 ${w} ${h}">`);
        }
      } catch (e) {
        console.warn('Could not parse SVG dimensions:', e);
      }
    }
    
    // Make width/height responsive (use 100% so viewBox controls scaling)
    if (wMatch) {
      svg = svg.replace(/(<svg[^>]*)\swidth="[^"]+"/, '$1 width="100%"');
    }
    if (hMatch) {
      svg = svg.replace(/(<svg[^>]*)\sheight="[^"]+"/, '$1 height="100%"');
    }
    
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    return URL.createObjectURL(blob);
  }, [svgContent]);

  useEffect(() => {
    return () => {
      if (svgDataUrl) URL.revokeObjectURL(svgDataUrl);
    };
  }, [svgDataUrl]);

  return (
    <>
      <div className="preview-header">
        <Typography.Title level={4} style={{ margin: 0, color: 'var(--text-primary)' }}>Live Preview</Typography.Title>
        <Space size="small" wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>Zoom: {Math.round(zoom * 100)}%</Text>
          {previewRefreshing && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Updating preview…
            </Text>
          )}
          <Button 
            icon={<ZoomInOutlined />} 
            onClick={handleZoomIn} 
            title="Zoom In (Ctrl/Cmd + Plus)"
            size="small"
            type="text"
          />
          <Button 
            icon={<ZoomOutOutlined />} 
            onClick={handleZoomOut} 
            title="Zoom Out (Ctrl/Cmd + Minus)"
            size="small"
            type="text"
          />
          <Button 
            icon={<RedoOutlined />} 
            onClick={handleZoomReset} 
            title="Reset Zoom (Ctrl/Cmd + 0)"
            size="small"
            type="text"
          />
          <Divider type="vertical" style={{ margin: '0 4px', height: 20 }} />
          {onSaveSVG && (
            <Button
              icon={<SaveOutlined />}
              onClick={onSaveSVG}
              disabled={!svgContent || savingSVG || exportLocked}
              loading={savingSVG}
              size="small"
              title={exportLocked ? 'Save this sign into the library (or update an existing saved sign) before exporting' : ''}
            >
              Save SVG
            </Button>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            disabled={!svgContent || exportLocked}
            size="small"
            title={
              exportLocked
                ? 'Download is available after Generate & save to library, or after Save changes on an existing saved sign'
                : ''
            }
          >
            Download SVG
          </Button>
        </Space>
      </div>

      <div className="preview-canvas" ref={containerRef}>
        {isLoading ? (
          <div className="preview-empty">
            <Spin size="large" />
            <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>Generating sign...</Text>
          </div>
        ) : svgDataUrl ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              padding: '20px',
              boxSizing: 'border-box',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <div
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            >
              <object
                data={svgDataUrl}
                type="image/svg+xml"
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.4))',
                }}
                aria-label="Generated road sign"
              >
                {/* Fallback: render inline with processed SVG */}
                <div 
                  style={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: '100%',
                  }}
                  dangerouslySetInnerHTML={{ __html: (() => {
                    // Process SVG for fallback too
                    let svg = svgContent;
                    const vbMatch = svg.match(/<svg[^>]*\sviewBox="([^"]+)"/);
                    const wMatch = svg.match(/<svg[^>]*\swidth="([^"]+)"/);
                    const hMatch = svg.match(/<svg[^>]*\sheight="([^"]+)"/);
                    if (!vbMatch && wMatch && hMatch) {
                      try {
                        const w = parseFloat(wMatch[1]);
                        const h = parseFloat(hMatch[1]);
                        if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
                          svg = svg.replace(/<svg([^>]*)>/, `<svg$1 viewBox="0 0 ${w} ${h}">`);
                        }
                      } catch (e) {}
                    }
                    if (wMatch) svg = svg.replace(/(<svg[^>]*)\swidth="[^"]+"/, '$1 width="100%"');
                    if (hMatch) svg = svg.replace(/(<svg[^>]*)\sheight="[^"]+"/, '$1 height="100%"');
                    return svg;
                  })() }} 
                />
              </object>
            </div>
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  No sign preview yet
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {livePreviewHint
                    ? livePreviewHint
                    : 'Click "Generate & save to library" to create your road sign'}
                </Text>
              </>
            }
          />
        )}
      </div>

      <div className="preview-info">
        <div className="info-item">
          <Text type="secondary" style={{ fontSize: 9 }}>Dimensions</Text>
          <Text strong style={{ fontSize: 12 }}>{signInfo.dimensions || '--'}</Text>
        </div>
        <div className="info-item">
          <Text type="secondary" style={{ fontSize: 9 }}>Stroke Width</Text>
          <Text strong style={{ fontSize: 12 }}>{signInfo.strokeWidth || '--'}</Text>
        </div>
        <div className="info-item">
          <Text type="secondary" style={{ fontSize: 9 }}>x-Height</Text>
          <Text strong style={{ fontSize: 12 }}>{signInfo.xHeight || '--'}</Text>
        </div>
        <div className="info-item">
          <Text type="secondary" style={{ fontSize: 9 }}>Sign Type</Text>
          <Text strong style={{ fontSize: 12 }}>{signInfo.signType || '--'}</Text>
        </div>
      </div>
    </>
  );
}
